import { useEffect, useState } from "react";
import DashboardCard from "../../../ui/DashboardCard/DashboardCard";
import { businessListingsApi } from "../../../../lib/businessListings";
import type { TeenBusinessListingApplicationListItemResponse } from "../../../../types/businessListingTypes";

type TeenBusinessApplicationsBoxProps = {
  className?: string;
};

type TeenBusinessApplicationDisplayStatus = "APPLIED" | "WITHDRAWN" | "REJECTED" | "EXPIRED";

const applicationStatusLabel: Record<TeenBusinessApplicationDisplayStatus, string> = {
  APPLIED: "Applied",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
  EXPIRED: "Expired",
};

const getDisplayStatus = (
  application: TeenBusinessListingApplicationListItemResponse,
): TeenBusinessApplicationDisplayStatus => {
  if (application.listingStatus === "EXPIRED") {
    return "EXPIRED";
  }
  if (application.applicationStatus === "WITHDRAWN") {
    return "WITHDRAWN";
  }
  if (application.applicationStatus === "REJECTED") {
    return "REJECTED";
  }
  return "APPLIED";
};

const TeenBusinessApplicationsBox = ({ className }: TeenBusinessApplicationsBoxProps) => {
  const [applications, setApplications] = useState<TeenBusinessListingApplicationListItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadApplications = async () => {
      try {
        setLoading(true);
        setHasError(false);
        const response = await businessListingsApi.listApplicationsForTeen();
        if (!cancelled) {
          setApplications(response);
        }
      } catch {
        if (!cancelled) {
          setHasError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadApplications();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <DashboardCard
      title="Business Applications"
      subtitle="Job applications you submitted to businesses."
      to="/teen/business-applications"
      className={className}
    >
      {loading ? (
        <p className="text-sm text-myteengig-text-muted">Loading business applications...</p>
      ) : hasError ? (
        <p className="text-sm text-myteengig-text-muted">Business applications are temporarily unavailable.</p>
      ) : applications.length === 0 ? (
        <p className="text-sm text-myteengig-text-muted">No business applications yet.</p>
      ) : (
        <ul className="space-y-3">
          {applications.slice(0, 3).map((application) => (
            <li key={application.applicationId} className="rounded-lg border border-myteengig-border bg-myteengig-background p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="min-w-0 font-semibold text-myteengig-text-primary">
                  {application.listingTitle ?? "Untitled Business Listing"}
                </p>
                <span className="rounded-full border border-myteengig-border bg-white px-2 py-1 text-xs font-semibold text-myteengig-text-primary">
                  {applicationStatusLabel[getDisplayStatus(application)]}
                </span>
              </div>
              <p className="mt-1 text-sm text-myteengig-text-muted">
                {application.businessName ?? "Business"}
              </p>
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
};

export default TeenBusinessApplicationsBox;
